import React, { useCallback, useImperativeHandle, useRef, useMemo, useEffect, forwardRef } from "react";
import Editor from './Editor';
import MergeFieldsPlugin from './MergeFieldsPlugin';
const EDITOR_ENTER_MODES = ['p', 'br', 'div'];
const EDITOR_ENTER_MODES_MAP = {
  'p': 1,
  'br': 2,
  'div': 3
};

// create the typescript for a list of size variants large med small

/*
  standard: allows most html tags, but disallows script tags and on-* attributes
  advanced: allows all html tags and attributes
  email: allows most html tags, but disallows script tags and on-* attributes, and is full page
  merge_field_only: only allows the merge field plugin to be used
*/

// type StandardVariantHTMLEditor = MergeFieldsPluginProps & {
//   variant: 'standard'
// }

// type EmailVariantHTMLEditor = MergeFieldsPluginProps & {
//   variant: 'email'
// }

// type MergeOnlyVariantHTMLEditor = MergeFieldsPluginProps & {
//   variant: 'merge_field_only'
// }

// type AllEmailVariants = StandardVariantHTMLEditor | EmailVariantHTMLEditor | MergeOnlyVariantHTMLEditor

// type VariantMapList = {
//   [key in AllEmailVariants['variant']]: EditorVariantOptions
// }

const standardVariantConfig = {
  toolbar: [{
    name: 'styles',
    groups: ['Format']
  }, {
    name: 'basicstyles',
    groups: ['basicstyles', 'cleanup']
  }, {
    name: 'paragraph',
    groups: ['align', 'list', 'blocks', 'bidi']
  }, {
    name: 'links',
    groups: ['Link', 'Unlink']
  }, {
    name: 'merge_field'
  }, {
    name: 'document',
    groups: ['mode', 'document', 'doctools']
  }, {
    name: 'insert'
  }, {
    name: 'editing',
    groups: ['find', 'selection', 'spellchecker']
  }],
  extraConfig: {
    allowedContent: 'p h1 h2 h3 h4 h5 h6 span div ul li ol s u em strong pre style br a[*](*){*}',
    // allow only text-based tags. allow all attributes, classes, and styles and allow script tags since a lot of companies have been using
    disallowedContent: 'p h1 h2 h3 h4 h5 h6 span div ul li ol s u em strong script iframe a[on*];',
    // disallow on-* attributes such as on-click 
    fullPage: false,
    startupShowBorders: false,
    enterMode: 1
  },
  removeButtons: ',Styles,Blockquote,Font,FontSize'
};
const getVariantConfig = (variant, props) => {
  switch (variant) {
    case 'standard':
      return standardVariantConfig;
    case 'advanced':
      // allows scripts and iframes to be inserted into the html
      let advancedVariantConfig = {
        ...standardVariantConfig
      };
      advancedVariantConfig.extraConfig.allowedContent = 'p h1 h2 h3 h4 h5 h6 span div ul li ol s u em strong pre table td tr th tbody thead style script iframe img br a[*](*){*}'; // allow only text-based tags. allow all attributes, classes, and styles and allow script tags since a lot of companies have been using
      advancedVariantConfig.extraConfig.disallowedContent = 'p h1 h2 h3 h4 h5 h6 span div ul li ol s u em strong a[on*];'; // disallow on-* attributes such as on-click
      return advancedVariantConfig;
    case 'email':
      return {
        toolbar: [{
          name: 'styles',
          groups: ['Format', 'font', 'fontsize']
        }, {
          name: 'colors'
        }, {
          name: 'others'
        }, {
          name: 'paste'
        }, {
          name: 'basicstyles',
          groups: ['basicstyles', 'cleanup']
        }, {
          name: 'paragraph',
          groups: ['list', 'indent', 'blocks', 'align', 'bidi']
        }, {
          name: 'links',
          groups: ['Link', 'Unlink']
        }, {
          name: 'insert'
        }, {
          name: 'editing',
          groups: ['find', 'selection', 'spellchecker']
        }, {
          name: 'merge_field'
        }, {
          name: 'document',
          groups: ['mode', 'document', 'doctools']
        }],
        extraConfig: {
          allowedContent: true,
          fullPage: true,
          startupShowBorders: false,
          enterMode: props.enterMode ? EDITOR_ENTER_MODES_MAP[props.enterMode] : 1
        },
        removeButtons: ',Styles,Blockquote'
      };
    case 'merge_field_only':
      return {
        toolbar: [{
          name: 'merge_field'
        }],
        extraConfig: {
          enterMode: props.enterMode ? EDITOR_ENTER_MODES_MAP[props.enterMode] : 1
        },
        plugins: props => [MergeFieldsPlugin(props.mergeFields, {
          ariaLabelMergeFieldDropdown: props.ariaLabelMergeFieldDropdown,
          ariaLabelMergeFieldOther: props.ariaLabelMergeFieldOther,
          onMergeFieldSelected: props.onMergeFieldSelected
        })]
      };
  }
};
export const HTMLEditor = /*#__PURE__*/forwardRef((props, forwardRef) => {
  var _editorRef$current, _editorRef$current3;
  const {
    variant = "standard"
  } = props;
  console.log("variant", variant);
  console.log("props", props);
  //const letVariant = !variant ? 'standard' : variant;

  const variantConfiguration = useMemo(() => getVariantConfig(variant, {
    enterMode: props.enterMode || 'p'
  }), [variant, props.mergeFields, props.enterMode]);
  const editorRef = useRef(null);
  useImperativeHandle(forwardRef, () => ({
    getValue: () => {
      return editorRef.current.getValueHTML();
    },
    setValue: (value, callback) => {
      editorRef.current.setValue(value, callback);
    },
    clear: () => {
      editorRef.current.clear();
    }
  }));
  const handlePluginsLoaded = payload => {
    const {
      editor
    } = payload;
    switch (variant) {
      case 'standard':
      case 'advanced':
      case 'email':
      case 'merge_field_only':
        const plugin = MergeFieldsPlugin(props.mergeFields, {
          toolbar: "merge_field",
          ariaLabelMergeFieldDropdown: props.ariaLabelMergeFieldDropdown,
          ariaLabelMergeFieldOther: props.ariaLabelMergeFieldOther,
          onMergeFieldSelected: props.onMergeFieldSelected
        });
        plugin(payload);
        break;
      default:
        break;
    }
    handleUpdateMergeFields(editor);
  };
  const handleChange = useCallback(payload => {
    if (editorRef !== null && editorRef !== void 0 && editorRef.current) {
      if (typeof props.onChange === 'function') {
        props.onChange(editorRef.current.getValueHTML());
      }
    }
  }, [editorRef, (_editorRef$current = editorRef.current) === null || _editorRef$current === void 0 ? void 0 : _editorRef$current.editor, props.onChange]);
  const handleUpdateMergeFields = useCallback(editor => {
    if (editor) {
      if (props.mergeFields && typeof editor.rebuildMergeFields === 'function') {
        editor.rebuildMergeFields(props.mergeFields);
      }
      if (props.mergeFields && typeof editor.updateMergeFieldsRef === 'function') {
        editor.updateMergeFieldsRef(props.mergeFields);
      }
    }
  }, [props.mergeFields]);
  useEffect(() => {
    var _editorRef$current2;
    handleUpdateMergeFields({
      editor: editorRef === null || editorRef === void 0 || (_editorRef$current2 = editorRef.current) === null || _editorRef$current2 === void 0 ? void 0 : _editorRef$current2.editor
    });
  }, [editorRef === null || editorRef === void 0 || (_editorRef$current3 = editorRef.current) === null || _editorRef$current3 === void 0 ? void 0 : _editorRef$current3.editor, handleUpdateMergeFields]);
  return /*#__PURE__*/React.createElement(Editor, {
    ref: editorRef,
    configuration: variantConfiguration,
    allowFullscreen: props.allowFullscreen,
    imageUploadURL: props.imageUploadURL,
    disabled: props.disabled,
    initialValue: props.initialValue,
    onChange: handleChange,
    onFileUploadRequest: props.onFileUploadRequest,
    onFileUploadResponse: props.onFileUploadResponse,
    onLoad: props.onLoad,
    variant: props.variant,
    onPluginsLoaded: handlePluginsLoaded
  });
});
export default HTMLEditor;